import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FlaskConical } from 'lucide-react';

const NEW = '__new';

export const SendToRDDialog = ({ open, onClose, bom }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(NEW);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(`Rework: ${bom.bom_variant_code} rev ${bom.bom_revision}`);
    setNotes('');
    setProjectId(NEW);
    api().get('/rd/projects').then(r => setProjects((r.data || []).filter(p => !['Released', 'Shelved'].includes(p.status)))).catch(() => {});
  }, [open, api, bom]);

  const submit = async () => {
    if (projectId === NEW && !name.trim()) { toast.error('Name the new project'); return; }
    setBusy(true);
    try {
      const body = projectId === NEW ? { new_project_name: name.trim(), notes } : { project_id: projectId, notes };
      const { data } = await api().post(`/boms/${bom.id}/send-to-rd`, body);
      toast.success(`Sent to R&D as ${data.draft_bom.revision}`);
      onClose();
      navigate(`/rd/${data.project.id}?tab=boms`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send to R&D failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="send-to-rd-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FlaskConical className="w-5 h-5 text-fuchsia-600" />Send to R&D Rework</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">
          A linked copy of <b>{bom.bom_variant_code} rev {bom.bom_revision}</b> goes into R&D as a draft. Production keeps using this revision,
          marked "In R&D Rework", until the reworked draft is promoted back as the next revision.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">R&D project</label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="send-to-rd-project-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NEW}>+ Create a new project</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {p.status}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {projectId === NEW && (
            <div>
              <label className="text-xs font-medium text-slate-600">New project name</label>
              <Input value={name} onChange={e => setName(e.target.value)} data-testid="send-to-rd-new-project-name" />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-600">Why is it being reworked? (optional)</label>
            <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Swap bracket supplier, reduce weld time" data-testid="send-to-rd-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="send-to-rd-submit-btn">
            <FlaskConical className="w-4 h-4 mr-1" />{busy ? 'Sending…' : 'Send to R&D'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
